import { HorizontalDivider, Input, Radio, Toggle, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CardContent, Section } from '@hero/atoms';
import { configs } from '@hero/configs';
import {
  AVAILABLE_OCCASION_NAMES,
  MAX_COST_CENTER_LENGTH,
  MAX_FEATURE_NAME_LENGTH,
  MAX_PO_LENGTH,
  NON_EDITABLE_AUTOMATION_STATUS,
  expirationDuration,
  scheduleDuration
} from '@hero/constants';
import { DetailItem, OnboardingCard, PaymentMethodInput } from '@hero/molecules';
import { OverviewAdvanceSettings } from '@hero/organisms';
import { useGetAutomation, useUpdateAutomation } from '@hero/services';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { useOnboardingStore } from '@hero/store/hooks';

import * as S from './style';

const TypeNameEditInput = ({ overviewData, setOverviewData }) => {
  const { t } = useTranslation();
  return (
    <Input
      value={overviewData.name}
      inputProps={{
        maxLength: MAX_FEATURE_NAME_LENGTH,
        onChange: e => setOverviewData({ ...overviewData, name: e.target.value })
      }}
      formItem={{
        label: t('featureNameLabel', { feature: t('automation') }),
        name: 'feature_name',
        validations: {
          required: {
            required: true
          }
        }
      }}
    />
  );
};

const PoEditInput = ({ overviewData, setOverviewData }) => {
  const { t } = useTranslation();
  return (
    <Input
      value={overviewData?.poNumber}
      inputProps={{
        maxLength: MAX_PO_LENGTH,
        onChange: e => setOverviewData({ ...overviewData, poNumber: e.target.value })
      }}
      formItem={{
        label: t('poLabel'),
        name: 'po'
      }}
    />
  );
};

const CostCenterEditInput = ({ overviewData, setOverviewData }) => {
  const { t } = useTranslation();
  return (
    <Input
      value={overviewData?.costCenter}
      inputProps={{
        maxLength: MAX_COST_CENTER_LENGTH,
        onChange: e => setOverviewData({ ...overviewData, costCenter: e.target.value })
      }}
      formItem={{
        label: t('costCenterLabel'),
        name: 'costCenter'
      }}
    />
  );
};

function AutomationReviewOverviewAccordion({ onClickOnboardingInfoCard }) {
  const { code } = useParams();
  const { t } = useTranslation();
  const { data: automation, isSuccess: isAutomationSuccess } = useGetAutomation({ code });
  const { mutateAsync: onUpdate, isLoading: isLoadingUpdateAutomation } = useUpdateAutomation();
  const onboarding = useOnboardingStore();
  const onboardingFeature = onboarding['automation'];
  const { data: user } = useGetUser();

  const [isOnboardingCardSettings, setIsOnboardingCardSettings] = useState(false);

  const [overviewData, setOverviewData] = useState({});

  const [advanceSettings, setAdvanceSettings] = useState({
    settings: {
      onlyCollectAddress: !!automation?.settings?.onlyCollectAddress,
      disableScratchEffect: !!automation?.settings?.disableScratchEffect
    }
  });

  useEffect(() => {
    if (isAutomationSuccess) {
      setOverviewData({
        name: automation?.name,
        poNumber: automation?.meta?.poNumber,
        costCenter: automation?.meta?.costCenter,
        paymentMethod: automation?.paymentMethod,
        disableReGifting: automation?.settings?.disableReGifting,
        allowPremiumDates: automation?.settings?.allowPremiumDates,
        requireScheduleMeeting: automation?.settings?.redeemRules?.requireScheduleMeeting,
        advanceSettings: {
          onlyCollectAddress: !!automation?.settings?.onlyCollectAddress,
          disableScratchEffect: !!automation?.settings?.disableScratchEffect
        },
        expiresAt: automation?.settings?.expiresAt,
        redemptionWindow: automation?.settings?.acceptanceDurationInHours,
        occasionScheduleDuration: automation?.settings?.occasionScheduleDuration?.value
      });

      setAdvanceSettings({
        settings: {
          onlyCollectAddress: automation.settings?.onlyCollectAddress,
          charityRules: automation.settings?.charityRules,
          disableScratchEffect: automation.settings?.disableScratchEffect
        }
      });

      if (!configs.advanceSettings.enabled && !automation.settings?.disableReGifting) {
        onUpdate({
          params: { code },
          body: {
            settings: {
              disableReGifting: true
            }
          }
        });
      }
    }
  }, [automation, isAutomationSuccess]);

  const messages = {
    requireMeeting: t('requireMeetingDesc'),
    itemReGift: t('itemReGiftDesc'),
    premiumDates: t('premiumDatesDesc')
  };

  const isDetailItemEditable = React.useMemo(
    () => !NON_EDITABLE_AUTOMATION_STATUS.includes(automation?.status),
    [automation]
  );

  function handlePaymentMethod(selectedPaymentMethod) {
    setOverviewData({ ...overviewData, paymentMethod: selectedPaymentMethod });

    onUpdate({
      params: { code },
      body: {
        paymentMethod: selectedPaymentMethod
      }
    });
  }

  const advanceSettingsEnabled = useMemo(() => {
    return (
      configs?.advanceSettings?.enabled &&
      (configs?.advanceSettings?.reGiftingEnabled ||
        configs?.advanceSettings?.charityEnabled ||
        configs?.advanceSettings?.premiumDates ||
        configs?.advanceSettings?.onlyCollectAddressEnabled ||
        configs?.advanceSettings?.scratchEffectEnabled)
    );
  }, [configs?.advanceSettings]);

  return (
    <S.OverviewWrapper>
      <CardContent>
        {configs.paymentMethods.enabled && isDetailItemEditable ? (
          <DetailItem title={`${t('paymentMethod')}:`} titleStyle={{ marginTop: '10px' }}>
            <PaymentMethodInput
              onTourButtonClick={() => setIsOnboardingCardSettings(true)}
              featureType="automation"
              selectedPaymentMethod={overviewData.paymentMethod}
              setSelectedPaymentMethod={handlePaymentMethod}
            />
          </DetailItem>
        ) : (
          <DetailItem title={`${t('paymentMethod')}:`}>
            {automation?.paymentMethod?.name}
          </DetailItem>
        )}

        <DetailItem
          titleIcon
          title={t('sendSchedule')}
          tooltipContent={t('sendScheduleTooltipContent')}
        >
          <S.RedemptionWindowWrapper>
            {scheduleDuration.map((item, index) => (
              <Radio
                key={`schedule_duration_${index.toString()}`}
                label={item.label}
                name="occasionScheduleDuration"
                value={item.value}
                checked={overviewData?.occasionScheduleDuration === item.value}
                disabled={isLoadingUpdateAutomation}
                onChange={async e => {
                  setOverviewData({ ...overviewData, occasionScheduleDuration: e.target.value });
                  await onUpdate({
                    params: { code },
                    body: {
                      settings: { occasionScheduleDuration: { unit: 'day', value: e.target.value } }
                    }
                  });
                }}
              />
            ))}
          </S.RedemptionWindowWrapper>
        </DetailItem>

        <DetailItem
          titleIcon
          title={`${t('redemptionWindow')}:`}
          tooltipContent={t('redemptionWindowTooltipContent')}
        >
          <S.RedemptionWindowWrapper>
            {expirationDuration.map((item, index) => (
              <Radio
                key={`expiration_duration_${index.toString()}`}
                label={item.label}
                name="acceptanceDurationInHours"
                value={item.value}
                checked={overviewData?.redemptionWindow === item.value}
                disabled={isLoadingUpdateAutomation}
                onChange={e => {
                  setOverviewData({ ...overviewData, redemptionWindow: e.target.value });
                  onUpdate({
                    params: { code },
                    body: {
                      settings: { acceptanceDurationInHours: e.target.value }
                    }
                  });
                }}
              />
            ))}
          </S.RedemptionWindowWrapper>
        </DetailItem>

        <DetailItem
          editable={false}
          bodyStyle={{ alignItems: 'center' }}
          title={`${t('automationType')}:`}
        >
          {t(AVAILABLE_OCCASION_NAMES[automation?.type])}
        </DetailItem>

        <DetailItem
          editable
          bodyStyle={{ alignItems: 'center' }}
          title={`${t('typeName', { type: t('automation') })}:`}
          closeWithoutSave={() => {
            setOverviewData({ ...overviewData, name: automation.name });
          }}
          saveDetail={() => {
            onUpdate({
              params: { code },
              body: {
                name: overviewData.name
              }
            });
          }}
          editInput={() => (
            <TypeNameEditInput overviewData={overviewData} setOverviewData={setOverviewData} />
          )}
        >
          {automation?.name}
        </DetailItem>

        <DetailItem
          editable={false}
          bodyStyle={{ alignItems: 'center' }}
          title={`${t('automationCode')}:`}
          withBorder
        >
          {code}
        </DetailItem>

        {!user?.meta?.isPunchoutUser && (
          <>
            <DetailItem
              editable
              title={`${t('po')}#:`}
              bodyStyle={{ alignItems: 'center' }}
              closeWithoutSave={() => {
                setOverviewData({ ...overviewData, poNumber: automation.meta?.poNumber });
              }}
              saveDetail={() => {
                onUpdate({
                  params: { code },
                  body: {
                    meta: {
                      poNumber: overviewData?.poNumber
                    }
                  }
                });
              }}
              editInput={() => (
                <PoEditInput overviewData={overviewData} setOverviewData={setOverviewData} />
              )}
            >
              {automation?.meta?.poNumber || t('addPurchaseOrderNumber')}
            </DetailItem>

            <DetailItem
              editable
              bodyStyle={{ alignItems: 'center' }}
              closeWithoutSave={() => {
                setOverviewData({ ...overviewData, costCenter: automation.meta?.costCenter });
              }}
              saveDetail={() => {
                onUpdate({
                  params: { code },
                  body: {
                    meta: {
                      costCenter: overviewData?.costCenter
                    }
                  }
                });
              }}
              editInput={() => (
                <CostCenterEditInput
                  overviewData={overviewData}
                  setOverviewData={setOverviewData}
                />
              )}
              title={`${t('costCenter')}:`}
            >
              {overviewData?.costCenter || t('addCostCenterId')}
            </DetailItem>
          </>
        )}

        {advanceSettingsEnabled && (
          <Section>
            <OnboardingCard
              styles={{
                wrapper: {
                  right: -12,
                  top: -12
                }
              }}
              position="right-start"
              cardOffset={[12, 12]}
              isCardShown={isOnboardingCardSettings}
              setIsCardShown={setIsOnboardingCardSettings}
              title={t('settings')}
              description={t('reviewAndPayOverviewOnboardingCard2Desc', {
                feature: t('automation').toLocaleLowerCase()
              })}
              isInfoActive={onboardingFeature?.infoCardsActive}
              featureType="automation"
              onTourButtonClick={onClickOnboardingInfoCard}
            />
            <Typography variant="body2Bold">{t('settings')}</Typography>
            {configs.advanceSettings.reGiftingEnabled && (
              <DetailItem title={`${t('itemReGifting')}:`} className="item-d-flex">
                <Typography variant="body2">{messages.itemReGift}</Typography>
                <Toggle
                  checked={!overviewData.disableReGifting}
                  disabled={isLoadingUpdateAutomation}
                  onChange={async () => {
                    setOverviewData({
                      ...overviewData,
                      disableReGifting: !overviewData.disableReGifting
                    });

                    onUpdate({
                      params: { code },
                      body: {
                        settings: {
                          disableReGifting: !overviewData.disableReGifting
                        }
                      }
                    });
                  }}
                />
              </DetailItem>
            )}
            {configs.advanceSettings.premiumDates !== false && (
              <DetailItem title={`${t('premiumDates')}:`} className="item-d-flex">
                {messages.premiumDates}
                <Toggle
                  checked={!!overviewData.allowPremiumDates}
                  disabled={isLoadingUpdateAutomation}
                  onChange={async () => {
                    setOverviewData({
                      ...overviewData,
                      allowPremiumDates: !overviewData.allowPremiumDates
                    });
                    onUpdate({
                      params: { code },
                      body: {
                        settings: {
                          allowPremiumDates: !overviewData.allowPremiumDates
                        }
                      }
                    });
                  }}
                />
              </DetailItem>
            )}
            {configs.advanceSettings.enabled && (
              <>
                <HorizontalDivider $margin="16px 0" />
                <OverviewAdvanceSettings
                  advanceSettings={advanceSettings}
                  setAdvanceSettings={({ settings: { disableScratchEffect } }) => {
                    setAdvanceSettings({
                      settings: {
                        disableScratchEffect
                      }
                    });

                    onUpdate({
                      params: {
                        code
                      },
                      body: {
                        settings: {
                          disableScratchEffect
                        }
                      }
                    });
                  }}
                />
              </>
            )}
          </Section>
        )}
      </CardContent>
    </S.OverviewWrapper>
  );
}

TypeNameEditInput.propTypes = {
  overviewData: PropTypes.shape({
    name: PropTypes.string
  }),
  setOverviewData: PropTypes.func
};

PoEditInput.propTypes = {
  overviewData: PropTypes.shape({
    poNumber: PropTypes.string
  }),
  setOverviewData: PropTypes.func
};

CostCenterEditInput.propTypes = {
  overviewData: PropTypes.shape({
    costCenter: PropTypes.string
  }),
  setOverviewData: PropTypes.func
};

AutomationReviewOverviewAccordion.propTypes = {
  onClickOnboardingInfoCard: PropTypes.func
};

export { AutomationReviewOverviewAccordion };
